/**
 * Global keyboard shortcuts (PLAN.md §5.10).
 *
 * The single most important rule here: **never steal a keystroke the user is typing into a
 * field.** A shortcut that fires while someone is writing a note is worse than no shortcut, so
 * every binding except the palette itself is suppressed inside inputs, textareas and
 * contenteditable regions.
 */

import { useEffect } from 'react';

export interface Hotkey {
  /** Lowercase `KeyboardEvent.key`, e.g. `'k'`, `'?'`, `'j'`. */
  key: string;
  /** Requires Ctrl (Windows/Linux) or Cmd (macOS). */
  meta?: boolean;
  handler: () => void;
  /**
   * Fire even while the user is typing. Only the palette sets this — Ctrl-K is unambiguous and
   * is exactly the shortcut people reach for *from* a field.
   */
  allowInInput?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useHotkeys(hotkeys: Hotkey[]): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      const modifier = event.metaKey || event.ctrlKey;

      for (const hotkey of hotkeys) {
        if (event.key.toLowerCase() !== hotkey.key.toLowerCase()) continue;
        if (Boolean(hotkey.meta) !== modifier) continue;
        if (typing && !hotkey.allowInInput) continue;
        // Alt-combinations are usually OS or browser bindings; don't shadow them.
        if (event.altKey) continue;

        event.preventDefault();
        hotkey.handler();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hotkeys]);
}
